import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Phone, HandHeart, Car, ShoppingBag, Sparkle, ForkKnife, TShirt, Pill, ShieldCheck, Clock, ArrowRight, List, X, CheckCircle } from "@phosphor-icons/react";
import { captureUtm, getUtm } from "../lib/utm";

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  { icon: HandHeart, title: "Personal care", text: "Bathing, grooming, dressing, moving safely through the day." },
  { icon: ForkKnife, title: "Meal preparation", text: "Meals cooked in their kitchen, the way they like them." },
  { icon: Sparkle, title: "Housekeeping", text: "Kitchens, bathrooms, floors — kept the way they keep them." },
  { icon: TShirt, title: "Laundry", text: "Washed, folded, put away. Fresh linens without the lifting." },
  { icon: Car, title: "Appointments", text: "A ride and a steady arm to the doctor, pharmacy, or lunch out." },
  { icon: ShoppingBag, title: "Shopping & errands", text: "Groceries, prescriptions, and the trips that keep a household running." },
  { icon: Pill, title: "Medication reminders", text: "On time, every time — the routine stays the routine." },
];

function LeadForm() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Just your name and phone number is all we need"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/request-care`, { ...form, who_for: "", payer: "Private Pay", utm: getUtm() });
      setSent(true);
    } catch { toast.error("Something went wrong — please call us at 800-719-6912"); }
    setBusy(false);
  };

  const inputCls = "w-full rounded-md border border-stone-300 bg-white px-4 py-3 text-base text-[#23282f] placeholder:text-stone-400 focus:border-[#6A3E97] focus:outline-none focus:ring-2 focus:ring-[#6A3E97]/20 transition-colors";

  if (sent) {
    return (
      <div className="rounded-xl border border-[#6A3E97]/20 bg-[#F6F1FB] p-8 text-center" data-testid="pp-form-success">
        <CheckCircle size={40} weight="fill" className="mx-auto text-[#6A3E97]" />
        <p className="mt-3 font-heading text-2xl font-medium text-[#23282f]">We'll call you shortly.</p>
        <p className="mt-1 text-base text-stone-600">A person from our Fairless Hills office — not a call center — will reach out. Need us sooner? <a href="tel:8007196912" className="font-semibold text-[#6A3E97]">800-719-6912</a>.</p>
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm sm:p-8" data-testid="pp-lead-form">
      <p className="font-heading text-2xl font-medium text-[#23282f]">Tell us what you need</p>
      <p className="mt-1 text-sm text-stone-500">Thirty seconds. We'll call you back with a straight answer and an exact rate in writing.</p>
      <div className="mt-5 space-y-3">
        <input className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} data-testid="pp-form-name" />
        <input className={inputCls} placeholder="Phone number" value={form.phone} onChange={set("phone")} data-testid="pp-form-phone" />
        <textarea className={inputCls + " min-h-[90px]"} placeholder="Who needs care, and what kind of help? (optional)" value={form.message} onChange={set("message")} data-testid="pp-form-message" />
        <button onClick={submit} disabled={busy}
          className="w-full rounded-md bg-[#6A3E97] px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#55317A] disabled:opacity-60"
          data-testid="pp-form-submit">
          {busy ? "Sending…" : "Have Silver Home Care call me"}
        </button>
        <p className="text-center text-xs text-stone-400">No obligation. No sales script. We answer questions and you decide.</p>
      </div>
    </div>
  );
}

export default function PrivatePayPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { captureUtm(); }, []);

  return (
    <div className="min-h-screen scroll-smooth bg-white text-[#23282f]" data-testid="private-pay-page">
      <header className="sticky top-0 z-50 border-b border-stone-200 bg-white" data-testid="pp-nav">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-10">
          <Link to="/"><img src="/shc-logo.png" alt="Silver Home Care" className="h-12 w-auto" /></Link>
          <nav className="hidden items-center gap-7 text-sm font-medium text-stone-600 md:flex">
            <a href="#why-rate" className="transition-colors hover:text-[#6A3E97]">Our rate</a>
            <a href="/#services" className="transition-colors hover:text-[#6A3E97]">Services</a>
            <a href="/#about" className="transition-colors hover:text-[#6A3E97]">About us</a>
            <a href="/reviews" className="transition-colors hover:text-[#6A3E97]">Reviews</a>
            <Link to="/free-care" className="font-semibold text-[#6A3E97] transition-colors hover:text-[#55317A]" data-testid="pp-nav-free">Free services</Link>
            <a href="/apply" className="transition-colors hover:text-[#6A3E97]">Apply</a>
            <a href="#contact" className="transition-colors hover:text-[#6A3E97]">Contact</a>
          </nav>
          <div className="flex items-center gap-3">
            <a href="tel:8007196912" className="inline-flex items-center gap-1.5 rounded-md bg-[#6A3E97] px-4 py-2.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#55317A]" data-testid="pp-call-cta">
              <Phone size={14} weight="fill" /> 800-719-6912
            </a>
            <button className="text-[#23282f] md:hidden" onClick={() => setMenuOpen(true)} aria-label="Menu" data-testid="pp-menu-open"><List size={26} /></button>
          </div>
        </div>
      </header>
      {menuOpen && (
        <div className="fixed inset-0 z-[60] bg-white" data-testid="pp-mobile-menu">
          <div className="flex items-center justify-between border-b border-stone-200 px-4 py-3">
            <img src="/shc-logo.png" alt="Silver Home Care" className="h-11 w-auto" />
            <button onClick={() => setMenuOpen(false)} aria-label="Close menu" data-testid="pp-menu-close"><X size={26} /></button>
          </div>
          <nav className="flex flex-col gap-1 px-6 py-6 text-lg font-medium">
            {[["#why-rate", "Our rate"], ["/#services", "Services"], ["/#about", "About us"], ["/reviews", "Reviews"], ["/apply", "Apply"], ["#contact", "Contact"]].map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)} className="rounded-lg px-3 py-3 transition-colors hover:bg-stone-50">{label}</a>
            ))}
            <Link to="/free-care" className="rounded-lg px-3 py-3 font-semibold text-[#6A3E97]" data-testid="pp-mobile-free">Free services — find out how →</Link>
            <a href="tel:8007196912" className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-[#6A3E97] px-5 py-3.5 text-base font-semibold text-white"><Phone size={16} weight="fill" /> 800-719-6912</a>
          </nav>
        </div>
      )}

      {/* Hero — mission first, price as proof */}
      <section className="border-b border-stone-200/70 bg-[#FBFAF8]">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14 lg:px-10 lg:py-20">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.15em] text-[#6A3E97]">Private-pay home care · Bucks & Philadelphia Counties</p>
            <h1 className="mt-4 font-heading text-4xl font-medium leading-[1.12] tracking-tight text-[#23282f] sm:text-5xl" data-testid="pp-hero-title">
              Help at home, starting at $27 an hour.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-stone-600">
              Meals, laundry, bathing, rides — dependable help from people who learn your routines and
              know your name. Silver Home Care is local, licensed, and ten years into serving Bucks and
              Philadelphia Counties.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <a href="#contact" className="inline-block rounded-md bg-[#6A3E97] px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#55317A]" data-testid="pp-hero-form-cta">
                Tell us what you need
              </a>
              <a href="tel:8007196912" className="inline-flex items-center gap-2 text-base font-semibold text-[#6A3E97] transition-colors hover:text-[#55317A]" data-testid="pp-hero-call-cta">
                <Phone size={18} weight="fill" /> Call 800-719-6912
              </a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-stone-500">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={15} className="text-[#6A3E97]" /> Licensed in Pennsylvania</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={15} className="text-[#6A3E97]" /> Celebrating ten years this October</span>
              <span className="inline-flex items-center gap-1.5"><Phone size={15} className="text-[#6A3E97]" /> A local office that answers</span>
            </div>
          </div>
          <img src="/purple-comfort.webp" alt="A Silver Home Care caregiver holding a client's hands at home"
            className="w-full rounded-xl object-cover shadow-lg" data-testid="pp-hero-img" />
        </div>
      </section>

      {/* Why the rate is different */}
      <section id="why-rate" className="border-b border-stone-200/70 bg-white">
        <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:py-16">
          <h2 className="text-center font-heading text-3xl font-medium tracking-tight text-[#23282f] sm:text-4xl" data-testid="pp-rate-heading">
            Yes — it starts at $27 an hour. Here's why.
          </h2>
          <div className="mt-10 space-y-8">
            <div className="flex gap-4">
              <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#6A3E97]/10 font-heading text-lg font-semibold text-[#6A3E97]">1</span>
              <div>
                <p className="text-lg font-semibold text-[#23282f]">It's a choice, not a catch.</p>
                <p className="mt-1 leading-relaxed text-stone-600">Care only works if a family can sustain it — for months and years, not just the first few weeks. We set our rate where real families can actually keep help in place. That decision is deliberate, and it's ours.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#6A3E97]/10 font-heading text-lg font-semibold text-[#6A3E97]">2</span>
              <div>
                <p className="text-lg font-semibold text-[#23282f]">Local, not franchise.</p>
                <p className="mt-1 leading-relaxed text-stone-600">No national royalty baked into your hourly rate. Our office is in Fairless Hills, and when you call, a person here picks up — not a call center three states away.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#6A3E97]/10 font-heading text-lg font-semibold text-[#6A3E97]">3</span>
              <div>
                <p className="mt-0 text-lg font-semibold text-[#23282f]">Your exact rate, in writing, before care starts.</p>
                <p className="mt-1 leading-relaxed text-stone-600">Every family's needs are different, so the final rate depends on the help required. We'll tell you exactly what it is — in writing — before anything begins. No surprises on the first invoice.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="border-b border-stone-200/70 bg-[#FBFAF8]">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-10">
          <h2 className="text-center font-heading text-3xl font-medium tracking-tight text-[#23282f] sm:text-4xl">What a visit can cover</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-lg text-stone-600">Non-medical help with everyday life — the tasks that keep someone safe, comfortable, and home.</p>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => (
              <div key={s.title} className="rounded-lg border border-stone-200/70 bg-white p-5" data-testid={`pp-service-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <s.icon size={22} weight="duotone" className="text-[#6A3E97]" />
                <h3 className="mt-2 font-heading text-lg font-medium text-[#23282f]">{s.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-stone-600">{s.text}</p>
              </div>
            ))}
            <div className="rounded-lg border border-[#6A3E97]/25 bg-[#F6F1FB] p-5">
              <HandHeart size={22} weight="duotone" className="text-[#6A3E97]" />
              <h3 className="mt-2 font-heading text-lg font-medium text-[#23282f]">Something else?</h3>
              <p className="mt-1 text-sm leading-relaxed text-stone-600">Ask. If it's non-medical and it helps them stay home, we can usually build it into the plan.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-b border-stone-200/70 bg-white">
        <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:py-16">
          <h2 className="text-center font-heading text-3xl font-medium tracking-tight text-[#23282f] sm:text-4xl">Getting started is a conversation, not a contract</h2>
          <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-3">
            {[
              ["Call or write", "Tell us what's going on. We'll listen, answer questions, and tell you honestly whether we can help."],
              ["We build the plan together", "What help, which days, what times — and your exact rate in writing before you decide anything."],
              ["Care starts", "A caregiver who fits — their routines, their preferences, their home. We stay involved for as long as you need us."],
            ].map(([t, d], i) => (
              <div key={t} data-testid={`pp-step-${i + 1}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#6A3E97] font-heading text-lg font-semibold text-white">{i + 1}</span>
                <h3 className="mt-3 font-heading text-xl font-medium text-[#23282f]">{t}</h3>
                <p className="mt-1 leading-relaxed text-stone-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free services — secondary banner */}
      <section className="border-b border-stone-200/70 bg-[#4A235A]">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-5 px-4 py-12 text-center sm:px-6">
          <p className="font-heading text-2xl font-medium leading-snug text-white sm:text-3xl" data-testid="pp-chc-banner">
            If the budget won't stretch at all — you may be eligible for <span className="text-[#D4AF6A]">free services.</span>
          </p>
          <p className="max-w-2xl text-base leading-relaxed text-white/80">
            Pennsylvania's Community HealthChoices program pays for home care for eligible families — and eligible
            families pay $0. It takes about a minute to check.
          </p>
          <Link to="/free-care" className="inline-flex items-center gap-2 rounded-md bg-[#D4AF6A] px-7 py-3 text-base font-semibold text-[#3A1B47] shadow-sm transition-colors hover:bg-[#E3C184]" data-testid="pp-chc-cta">
            Check eligibility — free <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="bg-[#FBFAF8]">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-16">
          <div>
            <h2 className="font-heading text-3xl font-medium tracking-tight text-[#23282f] sm:text-4xl">Talk to a person today</h2>
            <p className="mt-4 max-w-md text-lg leading-relaxed text-stone-600">
              Tell us what's happening at home. We'll give you a straight answer, an exact rate in writing,
              and all the time you need to decide.
            </p>
            <div className="mt-8 space-y-4">
              <a href="tel:8007196912" className="flex items-center gap-3" data-testid="pp-contact-phone">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#6A3E97]/10"><Phone size={20} weight="fill" className="text-[#6A3E97]" /></span>
                <span><span className="block font-semibold">800-719-6912</span><span className="text-sm text-stone-500">Weekdays 9–5 · a person answers</span></span>
              </a>
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#6A3E97]/10"><ShieldCheck size={20} className="text-[#6A3E97]" /></span>
                <span><span className="block font-semibold">Licensed in Pennsylvania</span><span className="text-sm text-stone-500">Non-medical home care · Bucks & Philadelphia Counties</span></span>
              </div>
            </div>
          </div>
          <LeadForm />
        </div>
      </section>

      <footer className="border-t border-stone-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-4 py-8 text-center sm:px-6">
          <img src="/shc-logo.png" alt="Silver Home Care" className="h-10 w-auto" />
          <p className="text-sm text-stone-500">680 Lincoln Hwy, Fairless Hills, PA 19030 · 800-719-6912 · silverhomecare.com</p>
          <div className="flex gap-5 text-sm">
            <Link to="/" className="text-stone-500 transition-colors hover:text-[#6A3E97]">Home</Link>
            <Link to="/free-care" className="font-semibold text-[#6A3E97]">Free services</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
